/**
 * O cabeçalho de uma planilha importada: em que linha ele está e que coluna é cada campo.
 *
 * A planilha vem de mãos diferentes, e cada uma escreve o título do seu jeito. "Código",
 * "CÓD.", "Cód. compras", "Código do item" e "Código de compras Evler" são a mesma coluna; a
 * coluna pode estar em qualquer posição, e o cabeçalho pode vir depois de um título e de uma
 * linha em branco. Antes, cada leitor (numerada, plana, por peça) tinha a sua lista de títulos
 * exatos, e o título fora da lista fazia a coluna sumir calada — o código de compras ia junto, e
 * o orçamento ficava sem preço sem que nada dissesse por quê.
 *
 * O reconhecimento, do mais forte para o mais fraco:
 *
 * 1. **O título inteiro** é um dos jeitos conhecidos de escrevê-lo — depois de tirar acento,
 *    caixa, pontuação, de abrir a abreviação ("qtd" é "quantidade") e de tirar a palavra de
 *    ligação ("código **do** item" é "código item");
 * 2. **o título contém** um dos jeitos conhecidos, com até duas palavras a mais ("Quantidade
 *    total", "Código de compras Evler");
 * 3. **uma palavra-chave** do campo aparece no título ("SKU do fornecedor" é código).
 *
 * Cada coluna serve a um campo só, e cada campo a uma coluna só: a ligação mais forte escolhe
 * primeiro, e no empate fica a coluna mais à esquerda. É o que separa "Código do item" (código,
 * pelo título inteiro) de "Item" (o material, também pelo título inteiro) na mesma linha.
 */

/** Sem acento, sem caixa, sem pontuação. `NFKD` desmonta o expoente: `kg/m³` vira `kg m3`. */
export function simplificarTitulo(valor: string): string {
  return valor
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLocaleLowerCase('pt-BR')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** As abreviações que as planilhas de obra usam, abertas na palavra inteira. */
const ABREVIACOES: Readonly<Record<string, string>> = {
  cod: 'codigo',
  codg: 'codigo',
  qtd: 'quantidade',
  qtde: 'quantidade',
  qde: 'quantidade',
  qt: 'quantidade',
  quant: 'quantidade',
  quantid: 'quantidade',
  und: 'unidade',
  unid: 'unidade',
  un: 'unidade',
  desc: 'descricao',
  descr: 'descricao',
  serv: 'servico',
  disc: 'disciplina',
  pav: 'pavimento',
  mat: 'material',
};

/** As palavras que ligam e não dizem nada: "código **do** item", "unidade **de** medida". */
const LIGACOES: ReadonlySet<string> = new Set([
  'a',
  'as',
  'o',
  'os',
  'e',
  'de',
  'do',
  'da',
  'dos',
  'das',
  'em',
  'no',
  'na',
  'nos',
  'nas',
  'para',
  'por',
]);

/** O título em palavras que se comparam: simplificado, abreviação aberta, ligação fora. */
export function palavrasDoTitulo(valor: string): readonly string[] {
  return simplificarTitulo(valor)
    .split(' ')
    .filter((palavra) => palavra !== '')
    .map((palavra) => ABREVIACOES[palavra] ?? palavra)
    .filter((palavra) => !LIGACOES.has(palavra));
}

/** Um campo do cabeçalho: os jeitos de escrevê-lo, e o que o reconhece ou o desqualifica. */
export interface CampoDoCabecalho {
  /** Os títulos conhecidos — escritos de qualquer jeito: passam pela mesma simplificação. */
  readonly titulos: readonly string[];
  /** A palavra que, em qualquer lugar do título, já diz que é este campo ("sku"). */
  readonly chaves?: readonly string[];
  /** A palavra que desqualifica o título: "Código SINAPI" não é o código de compras. */
  readonly exceto?: readonly string[];
}

/** Os campos de um leitor: a lista de títulos de cada um, ou o campo inteiro. */
export type CamposDoCabecalho<F extends string> = Readonly<
  Record<F, readonly string[] | CampoDoCabecalho>
>;

/** A coluna de cada campo; `-1` é o campo que a linha não tem. */
export type ColunasDoCabecalho<F extends string> = { readonly [K in F]: number };

const TITULO_INTEIRO = 100;
const PALAVRA_CHAVE = 40;
/** Até duas palavras a mais: "Quantidade total", "Código de compras Evler". */
const PALAVRAS_A_MAIS = 2;

interface CampoPronto {
  readonly formas: readonly (readonly string[])[];
  readonly chaves: readonly string[];
  readonly exceto: readonly string[];
}

function preparar(campo: readonly string[] | CampoDoCabecalho): CampoPronto {
  const definicao: CampoDoCabecalho = Array.isArray(campo)
    ? { titulos: campo as readonly string[] }
    : (campo as CampoDoCabecalho);
  return {
    formas: definicao.titulos
      .map((titulo) => palavrasDoTitulo(titulo))
      .filter((forma) => forma.length > 0),
    chaves: (definicao.chaves ?? []).flatMap((chave) => palavrasDoTitulo(chave)),
    exceto: (definicao.exceto ?? []).flatMap((palavra) => palavrasDoTitulo(palavra)),
  };
}

function mesmas(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((palavra, i) => palavra === b[i]);
}

/** Quão forte a coluna é este campo; `0` é "não é". */
function pontuar(titulo: readonly string[], campo: CampoPronto): number {
  if (titulo.length === 0) return 0;
  if (campo.exceto.some((palavra) => titulo.includes(palavra))) return 0;

  let melhor = 0;
  for (const forma of campo.formas) {
    if (mesmas(forma, titulo)) return TITULO_INTEIRO;
    const aMais = titulo.length - forma.length;
    if (aMais >= 0 && aMais <= PALAVRAS_A_MAIS && forma.every((p) => titulo.includes(p))) {
      // A forma mais longa contida diz mais; cada palavra a mais no título diz menos.
      melhor = Math.max(melhor, 50 + forma.length * 5 - aMais);
    }
  }
  if (melhor === 0 && campo.chaves.some((palavra) => titulo.includes(palavra))) {
    melhor = PALAVRA_CHAVE;
  }
  return melhor;
}

/**
 * Que coluna é cada campo, nesta linha.
 *
 * A ligação mais forte escolhe primeiro; cada coluna e cada campo entram numa ligação só.
 */
export function reconhecerColunas<F extends string>(
  campos: CamposDoCabecalho<F>,
  linha: readonly string[],
): ColunasDoCabecalho<F> {
  const nomes = Object.keys(campos) as F[];
  const prontos = new Map(nomes.map((nome) => [nome, preparar(campos[nome])] as const));
  const titulos = linha.map((celula) => palavrasDoTitulo(celula ?? ''));

  const ligacoes: { readonly campo: F; readonly coluna: number; readonly forca: number }[] = [];
  titulos.forEach((titulo, coluna) => {
    for (const nome of nomes) {
      const forca = pontuar(titulo, prontos.get(nome)!);
      if (forca > 0) ligacoes.push({ campo: nome, coluna, forca });
    }
  });
  ligacoes.sort((a, b) => b.forca - a.forca || a.coluna - b.coluna);

  const colunas = Object.fromEntries(nomes.map((nome) => [nome, -1])) as Record<F, number>;
  const ocupadas = new Set<number>();
  for (const { campo, coluna } of ligacoes) {
    if (colunas[campo] >= 0 || ocupadas.has(coluna)) continue;
    colunas[campo] = coluna;
    ocupadas.add(coluna);
  }
  return colunas;
}

/** Título, logotipo e linha em branco antes do cabeçalho são normais; cinquenta é de sobra. */
export const LINHAS_ATE_O_CABECALHO = 50;

/**
 * A primeira linha que tem todos os campos obrigatórios, e a coluna de cada campo nela.
 *
 * Procura nas primeiras `limite` linhas: o cabeçalho de verdade vem no topo, e procurar na
 * planilha inteira acharia, no meio dos dados, uma linha que por acaso escreve "Item" e
 * "Descrição".
 */
export function acharCabecalho<F extends string>(
  rows: readonly (readonly string[])[],
  campos: CamposDoCabecalho<F>,
  obrigatorios: readonly NoInfer<F>[],
  limite: number = LINHAS_ATE_O_CABECALHO,
): { readonly linha: number; readonly colunas: ColunasDoCabecalho<F> } | undefined {
  for (let linha = 0; linha < Math.min(rows.length, limite); linha += 1) {
    const colunas = reconhecerColunas(campos, rows[linha]!);
    if (obrigatorios.every((campo) => colunas[campo] >= 0)) return { linha, colunas };
  }
  return undefined;
}

/**
 * O código de compras, como as planilhas o escrevem.
 *
 * Compartilhado pelos três leitores: é a coluna que decide se o material tem preço (Fase 29), e
 * é a que mais muda de nome. "Código SINAPI", "TCPO" e as outras tabelas de referência não são o
 * código do sistema de compras — o título com uma delas não é esta coluna.
 */
export const CAMPO_CODIGO_DE_COMPRAS: CampoDoCabecalho = {
  titulos: [
    'codigo',
    'codigo de compras',
    'codigo compras',
    'codigo compra',
    'codigo do item',
    'codigo do produto',
    'codigo do material',
    'codigo do insumo',
    'codigo no compras',
    'codigo evler',
    'sku',
  ],
  chaves: ['codigo', 'sku'],
  exceto: ['sinapi', 'tcpo', 'orse', 'sicro', 'seinfra', 'composicao', 'servico', 'disciplina'],
};
